import { useMemo, useRef, useState } from 'react';
import { FaFileExcel, FaFileCsv, FaDownload, FaUpload, FaCheck, FaTimes, FaExclamationTriangle, FaTable, FaArrowRight } from 'react-icons/fa';
import { AppModal } from '../Modal/AppModal';
import { EmptyState, Progress } from '../Kit/Feedback';
import { Badge } from '../Kit/Display';
import { onExportCsv, onExportExcel } from './_tableExport';

const BLOCK = 'LStudio__app_import';

/*
    🔥 IMPORTAR Y EXPORTAR — modal de dos pestañas

    Portado de tu _ImportExportModal.js.

    ⚠️ EL ORDEN DE LOS PASOS NO ES CAPRICHOSO

    1. Descargar la PLANTILLA con las columnas correctas
    2. Subir el archivo lleno
    3. Ver el MAPEO y los errores ANTES de confirmar
    4. Recién ahí, importar

    Sin el paso 3, el usuario descubre que la columna "Provincia"
    estaba mal escrita cuando ya cargó 400 filas rotas. Mostrar el
    mapeo antes convierte un desastre en una corrección de dos minutos.

    ⚠️ LA VALIDACIÓN ES POR FILA, NO GLOBAL

    Un archivo con 200 filas donde 3 fallan no se rechaza entero: se
    importan las 197 buenas y se informan las 3. Rechazar todo obliga
    a rehacer el archivo por un error de tipeo.
*/

const REQUIRED_MARK = '*';

export const TableImportModal = ({
    isOpen,
    onClose,
    title = 'Importar y exportar',

    // exportación
    columns = [],
    rows = [],
    selectedRows = [],
    exportFileName = 'listado',

    // importación
    fields = [],                  // [{ key, label, isRequired, validate(value) }]
    templateRows = 50,
    onImport = null,              // async (rows) => void
    entityName = 'registros',
}) => {

    const REF__useRefFile = useRef(null);

    const [useTab, setTab] = useState('export');
    const [useScope, setScope] = useState('all');       // all | selected | filtered
    const [useFormat, setFormat] = useState('excel');
    const [useFileName, setFileName] = useState(null);
    const [useParsed, setParsed] = useState(null);      // { head, rows }
    const [useMapping, setMapping] = useState({});
    const [useIsWorking, setIsWorking] = useState(false);
    const [useProgress, setProgress] = useState(null);
    const [useResult, setResult] = useState(null);
    const [useError, setError] = useState(null);

    const EXPORT_ROWS = useScope === 'selected' ? selectedRows : rows;

    // ---------------------------- 🔥 EXPORTAR
    const onDoExport = () => {

        const NAME = `${exportFileName}_${new Date().toISOString().slice(0, 10)}`;

        if (useFormat === 'excel') return onExportExcel(columns, EXPORT_ROWS, `${NAME}.xlsx`, title);

        onExportCsv(columns, EXPORT_ROWS, `${NAME}.csv`);
    };

    /*
        🔥 PLANTILLA VACÍA

        ⚠️ Con filas de ejemplo, no vacía del todo. Una planilla con
           solo encabezados no dice qué formato espera cada columna;
           una fila de muestra sí.
    */
    const onDownloadTemplate = async () => {

        const XLSX = await import('xlsx');

        const HEAD = fields.map((f) => (f.isRequired ? `${f.label} ${REQUIRED_MARK}` : f.label));

        const SAMPLE = fields.map((f) => f.sample ?? '');

        const EMPTY = Array.from({ length: templateRows }, () => fields.map(() => ''));

        const SHEET = XLSX.utils.aoa_to_sheet([HEAD, SAMPLE, ...EMPTY]);

        SHEET['!cols'] = fields.map((f) => ({ wch: Math.max(f.label.length + 4, 16) }));

        const BOOK = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(BOOK, SHEET, 'Plantilla');

        XLSX.writeFile(BOOK, `plantilla_${exportFileName}.xlsx`);
    };

    // ---------------------------- 🔥 IMPORTAR
    const onPickFile = async (event) => {

        const FILE = event.target.files?.[0];

        if (!FILE) return;

        setError(null);
        setResult(null);
        setIsWorking(true);
        setFileName(FILE.name);

        try {

            const XLSX = await import('xlsx');

            const BUFFER = await FILE.arrayBuffer();

            const BOOK = XLSX.read(BUFFER, { type: 'array', cellDates: true });

            const SHEET = BOOK.Sheets[BOOK.SheetNames[0]];

            const MATRIX = XLSX.utils.sheet_to_json(SHEET, { header: 1, blankrows: false });

            if (MATRIX.length < 2) {
                setError('El archivo no tiene filas de datos.');
                return;
            }

            const HEAD = MATRIX[0].map((h) => String(h ?? '').replace(REQUIRED_MARK, '').trim());

            const BODY = MATRIX.slice(1).filter((line) => line.some((c) => c !== '' && c !== null));

            setParsed({ head: HEAD, rows: BODY });

            /*
                🔥 MAPEO AUTOMÁTICO
                Se intenta emparejar cada campo con una columna del
                archivo por nombre. Lo que no coincida queda para que
                el usuario lo elija a mano.
            */
            const AUTO = {};

            fields.forEach((field) => {

                const POS = HEAD.findIndex((h) => (
                    h.toLowerCase() === field.label.toLowerCase()
                    || h.toLowerCase() === field.key.toLowerCase()
                ));

                if (POS !== -1) AUTO[field.key] = POS;
            });

            setMapping(AUTO);

        } catch (err) {
            setError('No pudimos leer el archivo. ¿Es un Excel o CSV válido?');
        } finally {
            setIsWorking(false);
            if (REF__useRefFile.current) REF__useRefFile.current.value = '';
        }
    };

    /* 🔥 Valida fila por fila y separa buenas de malas */
    const MEMO__VALIDATION = useMemo(() => {

        if (!useParsed) return null;

        const OK = [];
        const BAD = [];

        useParsed.rows.forEach((line, index) => {

            const RECORD = {};
            const ERRORS = [];

            fields.forEach((field) => {

                const POS = useMapping[field.key];

                const VALUE = POS === undefined ? '' : line[POS];

                if (field.isRequired && (VALUE === '' || VALUE === null || VALUE === undefined)) {
                    ERRORS.push(`Falta ${field.label}`);
                }

                const CUSTOM = field.validate?.(VALUE);

                if (CUSTOM) ERRORS.push(CUSTOM);

                RECORD[field.key] = VALUE;
            });

            if (ERRORS.length) BAD.push({ line: index + 2, errors: ERRORS, data: RECORD });
            else OK.push(RECORD);
        });

        return { ok: OK, bad: BAD };

    }, [useParsed, useMapping, fields]);

    const onDoImport = async () => {

        if (!MEMO__VALIDATION?.ok.length) return;

        setIsWorking(true);
        setProgress(0);

        try {

            await onImport?.(MEMO__VALIDATION.ok, (percent) => setProgress(percent));

            setResult({ imported: MEMO__VALIDATION.ok.length, failed: MEMO__VALIDATION.bad.length });
            setParsed(null);

        } catch (err) {
            setError(err.message ?? 'Falló la importación.');
        } finally {
            setIsWorking(false);
            setProgress(null);
        }
    };

    const onReset = () => {
        setParsed(null);
        setMapping({});
        setResult(null);
        setError(null);
        setFileName(null);
    };

    return (
        <AppModal isOpen={isOpen} onClose={onClose} size="lg" title={title}
            description={`Descargá los ${entityName} o cargá una planilla`}
            footer={
                useTab === 'export'
                    ? (
                        <>
                            <button type="button" className={`${BLOCK}__btn_ghost`} onClick={onClose}>
                                Cerrar
                            </button>
                            <button type="button" className={`${BLOCK}__btn`} onClick={onDoExport}>
                                <FaDownload /> Descargar {EXPORT_ROWS.length}
                            </button>
                        </>
                    )
                    : (
                        <>
                            <button type="button" className={`${BLOCK}__btn_ghost`}
                                onClick={useParsed ? onReset : onClose} >
                                {useParsed ? 'Empezar de nuevo' : 'Cerrar'}
                            </button>
                            <button type="button" className={`${BLOCK}__btn`}
                                disabled={!MEMO__VALIDATION?.ok.length || useIsWorking}
                                onClick={onDoImport} >
                                <FaCheck /> Importar {MEMO__VALIDATION?.ok.length ?? 0}
                            </button>
                        </>
                    )
            } >

            {/* ==================== 🔥 PESTAÑAS ==================== */}
            <div className={`${BLOCK}__tabs`}>

                <button type="button" data-on={useTab === 'export'} onClick={() => setTab('export')}>
                    <FaDownload /> Exportar
                </button>

                <button type="button" data-on={useTab === 'import'} onClick={() => setTab('import')}>
                    <FaUpload /> Importar
                </button>

            </div>

            {/* ==================== 🔥 EXPORTAR ==================== */}
            {
                useTab === 'export' && (
                    <div className={`${BLOCK}__pane`}>

                        <p className={`${BLOCK}__label`}>Qué descargar</p>

                        <div className={`${BLOCK}__options`}>

                            <label data-on={useScope === 'all'}>
                                <input type="radio" name="scope" checked={useScope === 'all'}
                                    onChange={() => setScope('all')} />
                                <span>
                                    <b>Todo lo visible</b>
                                    <small>{rows.length} registros, con los filtros aplicados</small>
                                </span>
                            </label>

                            <label data-on={useScope === 'selected'} data-disabled={!selectedRows.length}>
                                <input type="radio" name="scope" checked={useScope === 'selected'}
                                    disabled={!selectedRows.length}
                                    onChange={() => setScope('selected')} />
                                <span>
                                    <b>Solo lo seleccionado</b>
                                    <small>
                                        {selectedRows.length
                                            ? `${selectedRows.length} registros marcados`
                                            : 'No hay nada marcado'}
                                    </small>
                                </span>
                            </label>

                        </div>

                        <p className={`${BLOCK}__label`}>Formato</p>

                        <div className={`${BLOCK}__options`}>

                            <label data-on={useFormat === 'excel'}>
                                <input type="radio" name="format" checked={useFormat === 'excel'}
                                    onChange={() => setFormat('excel')} />
                                <span>
                                    <b><FaFileExcel /> Excel (.xlsx)</b>
                                    <small>Con anchos de columna y formato</small>
                                </span>
                            </label>

                            <label data-on={useFormat === 'csv'}>
                                <input type="radio" name="format" checked={useFormat === 'csv'}
                                    onChange={() => setFormat('csv')} />
                                <span>
                                    <b><FaFileCsv /> CSV</b>
                                    <small>Texto plano, abre en cualquier planilla</small>
                                </span>
                            </label>

                        </div>

                        <p className={`${BLOCK}__hint`}>
                            Se exportan las columnas visibles. Si ocultaste alguna, no va a estar
                            en el archivo.
                        </p>

                    </div>
                )
            }

            {/* ==================== 🔥 IMPORTAR ==================== */}
            {
                useTab === 'import' && (
                    <div className={`${BLOCK}__pane`}>

                        {
                            useResult
                                ? (
                                    <EmptyState
                                        icon={<FaCheck />}
                                        title={`Se importaron ${useResult.imported} ${entityName}`}
                                        description={useResult.failed
                                            ? `${useResult.failed} filas quedaron afuera por errores.`
                                            : 'Todas las filas entraron sin problemas.'}
                                        action={
                                            <button type="button" className={`${BLOCK}__btn`} onClick={onReset}>
                                                Importar otro archivo
                                            </button>
                                        } />
                                )
                                : !useParsed
                                    ? (
                                        <>
                                            <ol className={`${BLOCK}__steps`}>
                                                <li>
                                                    <b>1. Descargá la plantilla</b>
                                                    <span>Trae las columnas correctas y una fila de ejemplo.</span>
                                                    <button type="button" className={`${BLOCK}__btn_ghost`}
                                                        onClick={onDownloadTemplate} >
                                                        <FaTable /> Descargar plantilla
                                                    </button>
                                                </li>
                                                <li>
                                                    <b>2. Completala</b>
                                                    <span>
                                                        Los campos con {REQUIRED_MARK} son obligatorios.
                                                        Borrá la fila de ejemplo antes de guardar.
                                                    </span>
                                                </li>
                                                <li>
                                                    <b>3. Subila acá</b>
                                                    <span>Vas a poder revisar todo antes de confirmar.</span>
                                                </li>
                                            </ol>

                                            <input ref={REF__useRefFile} type="file"
                                                accept=".xlsx,.xls,.csv"
                                                className={`${BLOCK}__file`} onChange={onPickFile} />

                                            <button type="button" className={`${BLOCK}__drop`}
                                                disabled={useIsWorking}
                                                onClick={() => REF__useRefFile.current?.click()} >
                                                <FaUpload />
                                                <span>{useIsWorking ? 'Leyendo…' : 'Elegí el archivo'}</span>
                                                <small>Excel o CSV</small>
                                            </button>
                                        </>
                                    )
                                    : (
                                        <>
                                            <div className={`${BLOCK}__summary`}>
                                                <span><b>{useFileName}</b></span>
                                                <Badge tone="ok">{MEMO__VALIDATION.ok.length} listos</Badge>
                                                {
                                                    MEMO__VALIDATION.bad.length > 0 && (
                                                        <Badge tone="danger">{MEMO__VALIDATION.bad.length} con errores</Badge>
                                                    )
                                                }
                                            </div>

                                            {/*
                                                🔥 EL MAPEO
                                                ⚠️ Editable a mano. El automático acierta casi siempre,
                                                   pero si el usuario renombró una columna hay que
                                                   poder corregirlo sin rehacer el archivo.
                                            */}
                                            <p className={`${BLOCK}__label`}>Correspondencia de columnas</p>

                                            <div className={`${BLOCK}__mapping`}>
                                                {
                                                    fields.map((field) => (
                                                        <div key={field.key} className={`${BLOCK}__map_row`}>

                                                            <span className={`${BLOCK}__map_field`}>
                                                                {field.label}
                                                                {field.isRequired && <em>{REQUIRED_MARK}</em>}
                                                            </span>

                                                            <FaArrowRight aria-hidden="true" />

                                                            <select value={useMapping[field.key] ?? ''}
                                                                onChange={(event) => setMapping({
                                                                    ...useMapping,
                                                                    [field.key]: event.target.value === ''
                                                                        ? undefined
                                                                        : Number(event.target.value),
                                                                })} >
                                                                <option value="">— Sin asignar —</option>
                                                                {
                                                                    useParsed.head.map((h, i) => (
                                                                        <option key={i} value={i}>{h || `Columna ${i + 1}`}</option>
                                                                    ))
                                                                }
                                                            </select>

                                                        </div>
                                                    ))
                                                }
                                            </div>

                                            {
                                                MEMO__VALIDATION.bad.length > 0 && (
                                                    <>
                                                        <p className={`${BLOCK}__label`}>
                                                            <FaExclamationTriangle /> Filas con problemas
                                                        </p>
                                                        <ul className={`${BLOCK}__errors`}>
                                                            {
                                                                MEMO__VALIDATION.bad.slice(0, 12).map((bad) => (
                                                                    <li key={bad.line}>
                                                                        <b>Fila {bad.line}</b>
                                                                        <span>{bad.errors.join(' · ')}</span>
                                                                    </li>
                                                                ))
                                                            }
                                                            {
                                                                MEMO__VALIDATION.bad.length > 12 && (
                                                                    <li><em>y {MEMO__VALIDATION.bad.length - 12} más…</em></li>
                                                                )
                                                            }
                                                        </ul>
                                                        <p className={`${BLOCK}__hint`}>
                                                            Estas filas NO se van a importar. Las demás sí.
                                                        </p>
                                                    </>
                                                )
                                            }

                                            {useProgress !== null && <Progress value={useProgress} label="Importando…" />}

                                        </>
                                    )
                        }

                        {
                            useError && (
                                <p className={`${BLOCK}__error`}>
                                    <FaTimes /> {useError}
                                </p>
                            )
                        }

                    </div>
                )
            }

        </AppModal>
    );
};
